import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Badge,
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material';
import { isNil } from 'lodash';
import React from 'react';

import { formatValue } from '../../utils/format-value';

const truncateValue = (value: unknown, maxLength = 200): string => {
  const formatted = formatValue(value);
  if (formatted.length <= maxLength) {
    return formatted;
  }

  // For very long values, show beginning and end with ellipsis in between
  const halfLength = Math.floor((maxLength - 3) / 2); // Account for " ... "
  const start = formatted.substring(0, halfLength);
  const end = formatted.substring(formatted.length - halfLength);
  return `${start} ... ${end}`;
};

interface TestCase {
  id: string;
  input: unknown;
  expected: unknown;
  actual?: unknown;
  status: 'passed' | 'failed' | 'pending' | 'timeout';
  error?: string;
  stdout?: string;
}

interface TestsOutputProps {
  testCases: TestCase[];
  currentTab?: number;
  onTabChange?: (tab: number) => void;
  consoleOutput?: string;
  hasNewConsoleOutput?: boolean;
  setHasUnviewedConsoleOutput?: (hasUnviewed: boolean) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <Box hidden={value !== index} style={{ height: '100%' }}>
      {value === index && children}
    </Box>
  );
};

export const TestsOutput: React.FC<TestsOutputProps> = ({
  testCases,
  currentTab = 0,
  onTabChange,
  consoleOutput,
  hasNewConsoleOutput = false,
  setHasUnviewedConsoleOutput,
}) => {
  const theme = useTheme();

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    onTabChange?.(newValue);

    // Clear badge when switching to console tab
    if (newValue === 1 && setHasUnviewedConsoleOutput) {
      setHasUnviewedConsoleOutput(false);
    }
  };

  const getStatusIcon = (status: TestCase['status']) => {
    switch (status) {
      case 'passed':
        return (
          <CheckCircleIcon
            sx={{ color: theme.palette.success.main, fontSize: 20 }}
          />
        );
      case 'failed':
        return (
          <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />
        );
      case 'timeout':
        return (
          <AccessTimeIcon
            sx={{ color: theme.palette.warning.main, fontSize: 20 }}
          />
        );
      case 'pending':
        return (
          <PlayArrowIcon
            sx={{ color: theme.palette.warning.main, fontSize: 20 }}
          />
        );
    }
  };

  const getStatusColor = (status: TestCase['status']) => {
    switch (status) {
      case 'passed':
        return theme.palette.success.main;
      case 'failed':
        return theme.palette.error.main;
      case 'timeout':
        return theme.palette.warning.main;
      case 'pending':
        return theme.palette.warning.main;
    }
  };

  const passedTests = testCases.filter(
    (test) => test.status === 'passed',
  ).length;
  const totalTests = testCases.length;

  const sortedTestCases = [...testCases].sort((a, b) => {
    const statusPriority = {
      failed: 0,
      timeout: 1,
      pending: 2,
      passed: 3,
    };
    return statusPriority[a.status] - statusPriority[b.status];
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Tabs */}
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 40,
            '& .MuiTab-root': {
              color: theme.palette.text.secondary,
              '&.Mui-selected': {
                color: theme.palette.text.primary,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <Tab
            label='Test Results'
            sx={{
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
            }}
          />
          <Tab
            label={
              hasNewConsoleOutput && currentTab !== 1 ? (
                <Badge
                  variant='dot'
                  color='primary'
                  sx={{
                    '& .MuiBadge-badge': {
                      position: 'relative !important',
                      backgroundColor: theme.palette.info.main,
                      animation: 'pulse 2s infinite',
                    },
                    '@keyframes pulse': {
                      '0%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.1)',
                        opacity: 0.7,
                      },
                      '100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                >
                  Console
                </Badge>
              ) : (
                'Console'
              )
            }
            sx={{
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 500,
            }}
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box
        sx={{
          height: '100%',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <TabPanel value={currentTab} index={0}>
          {/* Test Cases Tab */}
          <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
            {/* Test Summary */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                Test Summary:
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  color:
                    passedTests === totalTests
                      ? theme.palette.success.main
                      : theme.palette.error.main,
                }}
              >
                {passedTests}/{totalTests} passed
              </Typography>
            </Box>

            {sortedTestCases.map((testCase, index) => (
              <Paper
                key={testCase.id}
                elevation={1}
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: theme.palette.background.default,
                  border: `2px solid ${getStatusColor(testCase.status)}20`,
                  borderLeft: `4px solid ${getStatusColor(testCase.status)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getStatusIcon(testCase.status)}
                  <Typography
                    variant='subtitle1'
                    sx={{
                      ml: 1,
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {`Test Case ${index + 1}`}
                  </Typography>
                </Box>

                <Box sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  <Typography
                    component='div'
                    sx={{ mb: 1, color: theme.palette.text.primary }}
                  >
                    <strong>Input:</strong> {truncateValue(testCase.input)}
                  </Typography>
                  <Typography
                    component='div'
                    sx={{ mb: 1, color: theme.palette.text.primary }}
                  >
                    <strong>Expected:</strong> {formatValue(testCase.expected)}
                  </Typography>
                  {testCase.status !== 'pending' && (
                    <Typography
                      component='div'
                      sx={{
                        color:
                          testCase.status === 'passed'
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                      }}
                    >
                      <strong>Actual:</strong>{' '}
                      {isNil(testCase.actual)
                        ? ''
                        : formatValue(testCase.actual)}
                    </Typography>
                  )}
                  {testCase.stdout && (
                    <Box sx={{ mt: 1, overflow: 'auto', maxHeight: '200px' }}>
                      <Typography
                        component='div'
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.text.primary,
                        }}
                      >
                        Stdout:
                      </Typography>
                      <Typography
                        component='div'
                        sx={{
                          color: theme.palette.text.secondary,
                          whiteSpace: 'pre-wrap',
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? '#2d2d2d'
                              : '#f5f5f5',
                          p: 1,
                          borderRadius: 1,
                          mt: 0.5,
                        }}
                      >
                        {testCase.stdout}
                      </Typography>
                    </Box>
                  )}
                  {testCase.status === 'failed' && testCase.error && (
                    <Typography
                      component='div'
                      sx={{
                        color: theme.palette.error.main,
                        whiteSpace: 'pre-wrap',
                        mt: 1,
                      }}
                    >
                      <strong>Error:</strong> {testCase.error}
                    </Typography>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          {/* Console Tab */}
          <Box
            sx={{
              p: 2,
              backgroundColor:
                theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
              color: theme.palette.text.primary,
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              height: '100%',
              overflow: 'auto',
            }}
          >
            {consoleOutput ? (
              <Typography sx={{ whiteSpace: 'pre-wrap' }} component='div'>
                {consoleOutput}
              </Typography>
            ) : (
              <>
                <Typography
                  component='div'
                  sx={{ mb: 1, color: theme.palette.success.main }}
                >
                  $ Running tests...
                </Typography>
                {sortedTestCases.map((testCase, index) => (
                  <Typography
                    key={testCase.id}
                    component='div'
                    sx={{
                      mb: 0.5,
                      color: getStatusColor(testCase.status),
                    }}
                  >
                    Test {index + 1}: {testCase.status.toUpperCase()}
                  </Typography>
                ))}
                <Typography
                  component='div'
                  sx={{ mt: 2, color: theme.palette.success.main }}
                >
                  Tests completed: {passedTests}/{totalTests} passed
                </Typography>
                {consoleOutput && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      component='div'
                      sx={{
                        fontWeight: 'bold',
                        mb: 1,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Console Output:
                    </Typography>
                    <Typography
                      sx={{ whiteSpace: 'pre-wrap', color: '#fff' }}
                      component='div'
                    >
                      {consoleOutput}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>
      </Box>
    </Box>
  );
};
